import { useState } from "react";

const RngSimulator = () => {
  const [bountyCount, setBountyCount] = useState(1);
  const [simulationCount, setSimulationCount] = useState(100000);
  const [yourScore, setYourScore] = useState(0);
  const [result, setResult] = useState({});
  const [isRunning, setIsRunning] = useState(false);
  const [progress, setProgress] = useState(0);

  const runSimulation = async () => {
    setIsRunning(true);
    setResult({});
    setProgress(0);

    const resultMap = {};
    const batchSize = 1000; // Process in batches to update progress
    const totalBatches = Math.ceil(simulationCount / batchSize);

    for (let batch = 0; batch < totalBatches; batch++) {
      const currentBatchSize = Math.min(
        batchSize,
        simulationCount - batch * batchSize
      );

      for (let i = 0; i < currentBatchSize; i++) {
        const dropChances = {
          stage2: 0.3304,
          stage3: 0.3304,
          stage4: 0.2568,
          stage5: 0.3878,
          bonus: 0.3878,
        };
        let aya = 0;
        for (let j = 0; j < bountyCount; j++) {
          if (Math.random() < dropChances.stage2) aya++;
          if (Math.random() < dropChances.stage3) aya++;
          if (Math.random() < dropChances.stage4) aya++;
          if (Math.random() < dropChances.stage5) aya++;
          if (Math.random() < dropChances.bonus) aya++;
        }
        resultMap[aya] = (resultMap[aya] || 0) + 1;
      }

      // Update progress
      const currentProgress = ((batch + 1) / totalBatches) * 100;
      setProgress(currentProgress);

      // Allow UI to update
      if (batch < totalBatches - 1) {
        await new Promise((resolve) => setTimeout(resolve, 1));
      }
    }

    setResult(resultMap);
    setIsRunning(false);
    setProgress(0);
  };

  const mostProbable =
    Object.keys(result).length > 0
      ? Object.entries(result).reduce(
          (maxEntry, currentEntry) =>
            currentEntry[1] > maxEntry[1] ? currentEntry : maxEntry,
          [0, 0]
        )
      : null;

  const reset = () => {
    setBountyCount(1);
    setSimulationCount(100000);
    setYourScore(0);
    setResult({});
    setProgress(0);
  };

  return (
    <div className="min-h-screen py-8 font-lexend">
      <div className="max-w-4xl mx-auto px-4">
        <h1 className="text-4xl font-bold text-center mb-8">
          AYA SCORE SIMULATOR
        </h1>

        {/* Controls */}
        <div className="bg-white border border-gray-200 p-6 mb-6 shadow-sm">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label className="block text-gray-700 text-sm font-medium mb-2">
                Number of Bounties
              </label>
              <input
                type="number"
                min="1"
                max="1000"
                value={bountyCount}
                onChange={(e) => setBountyCount(parseInt(e.target.value) || 1)}
                className="w-full px-4 py-2 border border-gray-300 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Enter number of bounties"
              />
            </div>

            <div>
              <label className="block text-gray-700 text-sm font-medium mb-2">
                Number of Simulations
              </label>
              <input
                type="number"
                min="1000"
                max="1000000"
                step="1000"
                value={simulationCount}
                onChange={(e) => {
                  const value = parseInt(e.target.value) || 100000;
                  setSimulationCount(Math.min(value, 1000000));
                }}
                className="w-full px-4 py-2 border border-gray-300 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Enter simulation count"
              />
            </div>

            <div>
              <label className="block text-gray-700 text-sm font-medium mb-2">
                Your Score
              </label>
              <input
                type="number"
                min="0"
                max="1000"
                value={yourScore}
                onChange={(e) => setYourScore(parseInt(e.target.value) || 0)}
                className="w-full px-4 py-2 border border-gray-300 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Enter your score"
              />
            </div>
          </div>

          <div className="flex gap-4 mt-6">
            <button
              onClick={runSimulation}
              disabled={isRunning}
              className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white font-medium py-3 px-6 transition-colors duration-200"
            >
              {isRunning ? "Running Simulation..." : "Run Simulation"}
            </button>

            <button
              onClick={reset}
              disabled={isRunning}
              className="bg-gray-600 hover:bg-gray-700 disabled:bg-gray-400 text-white font-medium py-3 px-6 transition-colors duration-200"
            >
              Reset
            </button>
          </div>

          {/* Progress Bar */}
          {isRunning && (
            <div className="mt-4">
              <div className="flex justify-between text-sm text-gray-600 mb-2">
                <span>Progress</span>
                <span>{Math.round(progress)}%</span>
              </div>
              <div className="w-full bg-gray-200 h-2">
                <div
                  className="bg-blue-600 h-2 transition-all duration-100"
                  style={{ width: `${progress}%` }}
                ></div>
              </div>
            </div>
          )}
        </div>

        {/* Results */}
        {Object.keys(result).length > 0 && (
          <div className="bg-white border border-gray-200 p-6 shadow-sm">
            <div className="text-center mb-6">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                Simulation Results
              </h2>

              <div className="flex flex-col md:flex-row gap-4 justify-center">
                <div className="bg-blue-50 border border-blue-200 p-4">
                  <div className="text-blue-700 text-sm">
                    Most Probable Outcome
                  </div>
                  <div className="text-gray-900 text-2xl font-bold">
                    {mostProbable ? `${mostProbable[0]}` : "N/A"}
                  </div>
                  <div className="text-blue-700 text-sm">
                    {mostProbable
                      ? `${((mostProbable[1] / simulationCount) * 100).toFixed(
                          1
                        )}% chance`
                      : ""}
                  </div>
                </div>

                {yourScore > 0 && mostProbable && (
                  <div className="bg-green-50 border border-green-200 p-4">
                    <div className="text-green-700 text-sm">Your Score</div>
                    <div className="text-gray-900 text-2xl font-bold">
                      {yourScore}
                    </div>
                    <div className="text-green-700 text-sm">
                      {(() => {
                        const deviation = yourScore - parseInt(mostProbable[0]);
                        const deviationText =
                          deviation > 0
                            ? `+${deviation}`
                            : deviation === 0
                            ? "±0"
                            : `${deviation}`;
                        const yourChance = result[yourScore]
                          ? (
                              (result[yourScore] / simulationCount) *
                              100
                            ).toFixed(1)
                          : "0.0";
                        return `${deviationText} from most probable • ${yourChance}% chance`;
                      })()}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Results Table */}
            <div className="overflow-x-auto">
              <table className="w-full text-gray-900">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-2 font-semibold">Drops</th>
                    <th className="text-left py-3 px-2 font-semibold">
                      Occurrences
                    </th>
                    <th className="text-left py-3 px-2 font-semibold">
                      Probability
                    </th>
                    <th className="text-left py-3 px-2 font-semibold">
                      Visual
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(result)
                    .sort(([a], [b]) => parseInt(a) - parseInt(b))
                    .map(([drops, count]) => {
                      const percentage = (
                        (count / simulationCount) *
                        100
                      ).toFixed(2);
                      const barWidth =
                        (count / Math.max(...Object.values(result))) * 100;

                      return (
                        <tr key={drops} className="border-b border-gray-100">
                          <td className="py-2 px-2 font-semibold">{drops}</td>
                          <td className="py-2 px-2">
                            {count.toLocaleString()}
                          </td>
                          <td className="py-2 px-2">{percentage}%</td>
                          <td className="py-2 px-2 w-32">
                            <div className="bg-gray-200 h-4 overflow-hidden">
                              <div
                                className="h-full bg-blue-500 transition-all duration-300"
                                style={{ width: `${barWidth}%` }}
                              ></div>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {Object.keys(result).length === 0 && !isRunning && (
          <div className="bg-white border border-gray-200 p-12 text-center shadow-sm">
            <div className="text-gray-600 text-lg">
              Configure your settings above and click "Run Simulation" to see
              results
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RngSimulator;
